/**
 * Derivation of session dates from a project's recurring weekday schedule plus its
 * one-off overrides.
 *
 * A date is a session when the project's weekday list covers it and no `Skipped` override
 * removes it, or when an `Added` override introduces it. Added overrides win over the term
 * bounds so a make-up session outside the term is still trackable.
 */

import { dayOf, eachDay, isIsoDate, minDate, weekdayOf, withinRange } from './dates.js';
import { OverrideKind, type IsoDate, type Project, type SessionOverride } from './types.js';

/** Override lookup for one project, keyed by calendar day. */
export type OverrideIndex = ReadonlyMap<IsoDate, OverrideKind>;

/**
 * Indexes live overrides by date, keeping the most recently updated record per date.
 *
 * Soft-deleted overrides are excluded so removing an override restores the recurring rule.
 */
export function indexOverrides(overrides: readonly SessionOverride[]): OverrideIndex {
  const index = new Map<IsoDate, OverrideKind>();
  const seenAt = new Map<IsoDate, number>();
  for (const override of overrides) {
    if (override.deletedAt !== null) continue;
    const previous = seenAt.get(override.date);
    if (previous !== undefined && previous > override.updatedAt) continue;
    seenAt.set(override.date, override.updatedAt);
    index.set(override.date, override.kind);
  }
  return index;
}

/** Returns true when `date` is a session for `project` under `overrides`. */
export function isSessionDate(
  project: Pick<Project, 'weekdays' | 'termStart' | 'termEnd'>,
  date: IsoDate,
  overrides: OverrideIndex,
): boolean {
  const override = overrides.get(date);
  if (override === OverrideKind.Added) return true;
  if (override === OverrideKind.Skipped) return false;
  if (!withinRange(date, project.termStart, project.termEnd)) return false;
  return project.weekdays.includes(weekdayOf(date));
}

/**
 * Lists the session dates of `project` inside the inclusive `[from, to]` window, ascending.
 *
 * The window is required because an open-ended term would otherwise have no last date.
 */
export function listSessionDates(
  project: Pick<Project, 'weekdays' | 'termStart' | 'termEnd'>,
  overrides: OverrideIndex,
  from: IsoDate,
  to: IsoDate,
): IsoDate[] {
  if (to < from) return [];
  return eachDay(from, to).filter((date) => isSessionDate(project, date, overrides));
}

/**
 * Returns the first day a project can have a session on.
 *
 * An explicit `termStart` wins, so a project created mid-term can still backfill. Otherwise
 * sessions begin on the day the project was created; nothing is projected before that.
 */
export function projectStartDay(
  project: Pick<Project, 'termStart' | 'createdAt'>,
): IsoDate {
  return project.termStart ?? dayOf(project.createdAt);
}

/**
 * Lists every session of `project` from its first tracked day up to `horizonEnd`, ascending.
 *
 * The window opens at `projectStartDay`, or earlier if a dated override precedes it, so a
 * make-up session recorded before the term still appears. Returns an empty list when the
 * project has no recurring weekdays and no overrides.
 */
export function listAllSessionDates(
  project: Pick<Project, 'weekdays' | 'termStart' | 'termEnd' | 'createdAt'>,
  overrides: OverrideIndex,
  horizonEnd: IsoDate,
): IsoDate[] {
  const candidates: IsoDate[] = [projectStartDay(project)];
  for (const date of overrides.keys()) candidates.push(date);
  if (project.weekdays.length === 0 && overrides.size === 0) return [];
  const start = candidates.reduce(minDate);
  if (horizonEnd < start) return [];
  return listSessionDates(project, overrides, start, horizonEnd);
}

/**
 * Finds the session on or before `date`, falling back to the next session after it.
 *
 * Returns `null` when `sessions` is empty. Used to land the roll-call view on a sensible
 * day when the viewer opens the app between sessions.
 */
export function nearestSession(sessions: readonly IsoDate[], date: IsoDate): IsoDate | null {
  if (sessions.length === 0) return null;
  let best: IsoDate | null = null;
  for (const session of sessions) {
    if (session <= date) best = session;
    else if (best === null) return session;
    else break;
  }
  return best;
}

/** Returns the session immediately before `date`, or `null` when `date` is the first. */
export function previousSession(sessions: readonly IsoDate[], date: IsoDate): IsoDate | null {
  let previous: IsoDate | null = null;
  for (const session of sessions) {
    if (session >= date) break;
    previous = session;
  }
  return previous;
}

/** Returns the session immediately after `date`, or `null` when `date` is the last. */
export function nextSession(sessions: readonly IsoDate[], date: IsoDate): IsoDate | null {
  for (const session of sessions) if (session > date) return session;
  return null;
}

/** Parses a persisted term bound, treating empty strings and malformed values as unbounded. */
export function parseTermBound(value: unknown): IsoDate | null {
  return isIsoDate(value) ? value : null;
}
